import { FIT_DEFAULT } from '../config/videoSettings';

import type { ElementState } from '../renderer/ElementState';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { VideoSource } from '@src/stores-v2/types';

const FIELDS_TO_NOT_COMPARE = [
  'time',
  'track',
  'duration',
  'trim_start',
  'color_overlay',
  'animations',
];

export const FIELDS_POSITIONAL = [
  'x',
  'y',
  'width',
  'height',
  'x_anchor',
  'x_alignment',
  'y_anchor',
  'y_alignment',
];

const SOURCE_FIELDS_DEFAULTS: Record<string, string> = {
  fit: FIT_DEFAULT,
  x: '50%',
  y: '50%',
  width: '100%',
  height: '100%',
  x_alignment: '50%',
  y_alignment: '50%',
  x_anchor: '50%',
  y_anchor: '50%',
};

type SourceDiff = Record<
  string,
  {
    track: number;
    changes: Record<string, unknown>;
  }
>;

export type SourceDiffEntry = SourceDiff[string];

export const getOriginalVideoTrackSourceDiff = (
  videoCreator: VideoCreatorStore,
  prevTracksState: ElementState[],
  newTracksState: ElementState[],
): SourceDiff => {
  const diff: SourceDiff = {};
  collectFrameLockedElements(videoCreator, newTracksState).forEach(
    trackState => {
      const prevSource = findElementStateById(
        videoCreator,
        prevTracksState,
        trackState.source.id,
      )?.source;
      if (!prevSource) return;
      for (const key in { ...trackState.source, ...prevSource }) {
        if (FIELDS_TO_NOT_COMPARE.includes(key)) {
          continue;
        }

        if (prevSource && prevSource[key] !== trackState.source[key]) {
          diff[trackState.source.id] = diff[trackState.source.id] || {
            track: trackState.track,
            changes: {},
          };
          if (
            !Object.prototype.hasOwnProperty.call(trackState.source, key) &&
            SOURCE_FIELDS_DEFAULTS[key]
          ) {
            diff[trackState.source.id].changes[key] =
              SOURCE_FIELDS_DEFAULTS[key];
          } else {
            diff[trackState.source.id].changes[key] =
              trackState.source[key] || SOURCE_FIELDS_DEFAULTS[key] || null;
          }
        }
      }
    },
  );
  return diff;
};

/**
 * Can find inner elements of compositions
 */
export function findElementStateById(
  videoCreator: VideoCreatorStore,
  elements: ElementState[],
  targetId: string,
): ElementState | undefined {
  return (
    videoCreator.renderer?.findElement(
      elementState => elementState.source.id === targetId,
      { elements: elements, source: {} },
    ) ||
    // fallback to shallow find if renderer is not available
    elements.find(elementState => elementState.source.id === targetId)
  );
}

function collectFrameLockedElements(
  videoCreator: VideoCreatorStore,
  newTracksState: ElementState[],
): ElementState[] {
  return newTracksState.filter(trackState =>
    videoCreator.frameLockedTracks.includes(trackState.track),
  );
}

export const getElementPositionProperties = (
  time: number,
  element: ElementState,
) => {
  const positionProperties: Record<string, any> = {};
  let scaleFactor = 1;
  if (element.source.animations?.length) {
    const currentPlayheadTime = time;
    const animation = element.source.animations[0];
    // assumes animation is from start to end of element time
    // const animationStartTime = animation.time || 0 + (element.globalTime || 0);
    // const animationEndTime = animation.duration

    // assumes linear easing
    if (element.globalTime > currentPlayheadTime && animation.start_scale) {
      scaleFactor = parseFloat(animation.start_scale) / 100;
    } else if (
      element.globalTime + element.duration < currentPlayheadTime &&
      animation.end_scale
    ) {
      scaleFactor = parseFloat(animation.end_scale) / 100;
    } else if (animation.start_scale || animation.end_scale) {
      const scalePerSecond =
        (parseFloat(animation.end_scale || '100') -
          parseFloat(animation.start_scale || '100')) /
        element.duration;
      scaleFactor =
        (parseFloat(animation.start_scale || '100') +
          scalePerSecond * (currentPlayheadTime - element.globalTime)) /
        100;
    }
  }

  for (const key of FIELDS_POSITIONAL) {
    positionProperties[key] =
      element.source[key] || SOURCE_FIELDS_DEFAULTS[key] || null;
    if (
      (scaleFactor !== 1 && positionProperties[key] && key === 'width') ||
      key === 'height'
    ) {
      positionProperties[key] = `${
        parseFloat(positionProperties[key]) * scaleFactor
      }%`;
    }
  }
  return positionProperties;
};

export const hydrateOriginalMediaDuration = (
  videoSource: VideoSource,
  videoCreator: IVideoCreatorStore,
): VideoSource => {
  const story = videoCreator.story;
  const fullMediaDuration: number | undefined =
    story?.originalVideo?.video?.duration;
  if (typeof fullMediaDuration !== 'number' || fullMediaDuration <= 0) {
    return videoSource;
  }
  if (!videoSource.elements) return videoSource;
  for (const element of videoSource.elements) {
    if (
      element.media_duration == null &&
      videoCreator.isOriginalVideoElement(element)
    ) {
      element.media_duration = fullMediaDuration;
    }
  }
  return videoSource;
};
